import { Listbox } from '@headlessui/react';
import { FormEvent, useState } from 'react';

import clsxm from '@/lib/clsxm';
import useDisciplines from '@/hooks/use-disciplines';
import { useGlobalContext } from '@/hooks/use-global-context';

import { IconSize } from '@/components/icons/base-icon';
import ChevronDownIcon from '@/components/icons/chevron-down-icon';
import { LinkVariant, LinkVariantStyles } from '@/components/link';
import Typography, { TypographyColor } from '@/components/typography';

import { Discipline } from '@/services/disciplines.service';
import { isParamNurse } from '@/utils/discipline-utils';

const ALL_DISCIPLINES = 'all';

const getDisciplineOptionLabel = (discipline: Discipline | null) => {
  if (!discipline) {
    return '';
  }

  if (isParamNurse(discipline.apiId)) {
    return 'Nurse';
  }

  return discipline.title;
};

type SearchJobsByDisciplineProps = {
  className?: string;
  dropShadow?: boolean;
};
const SearchJobsByDiscipline = (props: SearchJobsByDisciplineProps) => {
  const disciplines = useDisciplines();
  const disciplineOptions: Discipline[] = [...disciplines];

  const { pageStaticData } = useGlobalContext();
  const jobCategoryPageRoutes = pageStaticData?.jobCategoryPageRoutes || {};

  const [selectedDiscipline, setSelectedDiscipline] = useState(
    disciplineOptions.find((option) => isParamNurse(option.apiId)) || null
  );
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      selectedDiscipline?.slug &&
      selectedDiscipline.slug in jobCategoryPageRoutes
    ) {
      window.location.assign(jobCategoryPageRoutes[selectedDiscipline.slug]);
      return;
    }

    // If we dont have a route, send them to search with the discipline pre-filtered
    let query = '';
    if (selectedDiscipline?.slug !== ALL_DISCIPLINES) {
      query = `?discipline=${selectedDiscipline?.apiId}`;
    }
    window.location.assign(`/jobs/search${query}`);
  };

  return (
    <form
      className={clsxm(
        'w-full md:rounded-full md:bg-white md:p-4 md:px-8',
        props.dropShadow && 'md:shadow-md'
      )}
      onSubmit={handleSubmit}
    >
      <div
        className={clsxm(
          'flex w-full flex-col justify-between md:flex-row lg:max-w-[50vw] 2xl:max-w-[40vw]',
          props.className
        )}
      >
        <div className='hidden items-center md:flex'>
          <Typography.Body2
            color={TypographyColor.LIGHT}
            weight='bold'
            className='whitespace-nowrap'
          >
            Choose Your Discipline:
          </Typography.Body2>
        </div>
        <div className='mt-1 flex h-16 rounded-full border-2 border-solid border-gray-200 bg-white p-1 pl-8 sm:mt-0 md:h-auto md:border-0'>
          <Listbox onChange={setSelectedDiscipline}>
            <Listbox.Button className='mr-8 flex w-full flex-grow items-center justify-between gap-2'>
              <div className='flex flex-col gap-y-1 py-2 text-left'>
                <Typography.Body2 className='!break-keep leading-[24.8px] text-gray-600'>
                  {getDisciplineOptionLabel(selectedDiscipline)}
                </Typography.Body2>
              </div>
              <ChevronDownIcon
                className='text-gray-800'
                size={IconSize.SMALL}
              />
            </Listbox.Button>
            <Listbox.Options
              className={clsxm(
                'absolute z-10 flex cursor-pointer flex-col bg-white shadow-lg',
                'left-0 right-0 mt-10 w-full md:left-auto md:right-auto  md:w-auto', // mobile positioning and width
                'select-scrollbar max-h-80 overflow-y-auto' // scroll
              )}
              unmount={false}
            >
              {disciplineOptions.map((discipline) => (
                <Listbox.Option
                  key={discipline.slug}
                  value={discipline}
                  className={clsxm('px-4 py-4 hover:bg-orchid-notification', {
                    'bg-orchid-notification':
                      selectedDiscipline?.slug === discipline.slug,
                  })}
                >
                  <Typography.Body2>
                    {getDisciplineOptionLabel(discipline)}
                  </Typography.Body2>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>
        </div>

        <div className='flex'>
          <button
            type='submit'
            className={clsxm(
              LinkVariantStyles[LinkVariant.PRIMARY_BUTTON],
              'mx-auto mt-4 h-full w-full md:mt-0 lg:mx-0 lg:w-auto'
            )}
          >
            Search Jobs
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchJobsByDiscipline;
